import React from 'react';
import { Box, Button, Card, CardContent, Container, darken, Grid2, Typography } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ExpandableParagraph } from '../../../components/ExpandableParagraph';
import ToolShed from '../../../components/ToolShed';
import { PlanDetails, PlanAuthor, Tool, QuickOrderCost } from '../types';
import { CutsBlock } from './PlanViewInfoTabCutsBlock';
import useAuth from '../../../components/Auth';
import PinterestEmbed from '../../../components/PinterestEmbed';
import { PlanAuthorCaption } from '../PlanView';
import { PlanPrimaryInfoSection } from './PlanPrimaryInfoSection';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import { AuthorBlock } from '../../../components/AuthorBlock';

interface PlanViewInfoTabPinterestContentProps {
    plan: PlanDetails;
    planAuthor: PlanAuthor;
    companyTools: Tool[];
    quickOrderCosts: QuickOrderCost[];
    handleViewCutsClick: () => void;
    handleAddTool: (toolId: number) => void;
    handleRemoveTool: (toolId: number) => void;
}

export const PlanViewInfoTabPinterestContent = ({plan, planAuthor, companyTools, quickOrderCosts, handleViewCutsClick, handleAddTool, handleRemoveTool}: PlanViewInfoTabPinterestContentProps) => {
	const theme = useTheme();
	const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
	const {userProfile} = useAuth();
	const companyToolsSet = new Set(companyTools.map((tool) => tool.id));
	const {t} = useTranslation();

	return <>
		<Container disableGutters={!isMdUp}>
			<Grid2 container direction={isMdUp ? 'row' : 'column-reverse'} spacing={2}>
				<Grid2 size={{xs: 12, md: 'auto'}}>
					<Card elevation={0}>
						<CardContent>
							{plan.pinId && <PinterestEmbed pinId={plan.pinId} />}
							{plan.description && <Box sx={{mt:2}}>
								<ExpandableParagraph text={plan.description} />
							</Box>}
						</CardContent>
						<Box display="flex" flexDirection="column" alignItems="center" gap={1} sx={{p: 2, backgroundColor: darken(theme.palette.background.paper, 0.05)}}>
							<PlanAuthorCaption authorName={plan.author.name || ''} source={plan.source} />
							<Typography variant='caption' color='text.secondary'>{t('planView.notAffiliatedWithAuthor')}</Typography>
						</Box>
					</Card>
				</Grid2>
				<Grid2 size={{xs: 12, md: 'grow'}}>
					<Container disableGutters={isMdUp}>
						<PlanPrimaryInfoSection 
							planId={plan.id}
							quickOrderCosts={quickOrderCosts}
							timeCost={plan.timeCost}
							ratingsSummary={plan.ratingsSummary}
							isBuyNowEnabled={Boolean(quickOrderCosts.length > 0)}
							tags={plan.tags}
						/>
					</Container>
				</Grid2>
			</Grid2>
		</Container>
		<Container>
			<Grid2 container>
				{plan.cutsUrl && <Grid2 size={12}>
					<CutsBlock cutsUrl={plan.cutsUrl} onClick={handleViewCutsClick} />
				</Grid2>}
				{Boolean(plan.tools && plan.tools.length > 0) && <Grid2 size={{xs: 12, md: 8}} mt={5} offset={{xs: 0, md: 2}}>
					<ToolShed 
						title={t('planViewMaterialsCard.toolsUsed')}
						tools={plan.tools.map((tool) => tool.tool ? tool.tool : ({
							key: tool.name,
						}))} 
						companyToolsSet={userProfile ? companyToolsSet : undefined}
						onAddTool={userProfile ? handleAddTool : undefined}
						onRemoveTool={userProfile ? handleRemoveTool : undefined}
						sx={{mt: 4}}
					/>
				</Grid2>}
				{/* Currently not showing author block for non-affiliated plans */}
				{/* <Grid2 size={12} sx={{mt:12}}>
					<AuthorBlock authorInfo={planAuthor} />
				</Grid2> */}
			</Grid2>
			<Box display='flex' justifyContent='center' sx={{mt: 5}}>
				<Button component={Link} variant='contained' to={`/plans/${plan.id}/supplies`} size='large' color='primary' endIcon={<ArrowForwardIcon />}>
					{t('planView.toSuppliesTab')}
				</Button>
			</Box>
			<Box mt={12}>
				<AuthorBlock authorInfo={planAuthor} />
			</Box>
		</Container>
	</>;
};